import { useSnackbar } from 'notistack';

import { DocumentType, PackageTypeId } from '@models/document';
import { TQuoteApiResponse } from '@models/quotes';
import { Action, initialDialogState } from '@pages/QuoteDetails/constants/QuoteDetails.constants';

import { ToastType } from '@components/toast/ToastType.enum';
import { useUpdateQuote } from '@services/quotes';
import { IDialogState } from '@pages/QuoteDetails/types';
import { useDownloadQuoteDocument } from '@components/button/DownloadApplicationButton';
import { useLayoutStore } from '@store/LayoutStore';
import { DrawerList } from '@pages/QuoteDetails/constants/DrawerList.enum';
import { shallow } from 'zustand/shallow';
import { useCurrentQuoteStoreState } from '@store/Quote/QuoteStore';

export const useGetActionMap = ({
  setDialogState,
  handleOpenReactivateQuoteDialog,
  onConfirmExit,
  toggleDrawer,
  isMobile,
  saveChanges,
  quote,
  quoteId,
  isFullScreenMode,
  setActiveTab,
}: {
  setDialogState: React.Dispatch<React.SetStateAction<IDialogState>>;
  handleOpenReactivateQuoteDialog: () => void;
  onConfirmExit: () => void;
  toggleDrawer: (drawer: DrawerList) => void;
  isMobile: boolean;
  saveChanges: () => void;
  quote: TQuoteApiResponse;
  quoteId: string;
  isFullScreenMode?: boolean;
  setActiveTab?: (tab: DrawerList) => void;
}) => {
  const { updateQuote } = useUpdateQuote();
  const { enqueueSnackbar } = useSnackbar();

  const { openHeaderMenu, setSecondaryMenuTab } = useLayoutStore(
    (state) => ({
      openHeaderMenu: state.openHeaderMenu,
      setSecondaryMenuTab: state.setSecondaryMenuTab,
    }),
    shallow,
  );

  const quoteContextStore = useCurrentQuoteStoreState();

  const { handleDownloadQuoteDocument } = useDownloadQuoteDocument({
    quoteId,
    createdOnOrAfterDate: quote?.updatedAt,
    downloadType: 'auto',
    documentTypeId: DocumentType.Application.id,
    packageTypeId: PackageTypeId.Application,
  });

  const handleOpenCancelQuoteDialog = (quoteId: string, policyNumber: string) => {
    setDialogState((prev) => ({
      ...prev,
      cancelQuoteDetails: { cancelQuoteId: quoteId, cancelQuotePolicyNumber: policyNumber },
    }));
  };

  const handleOpenCopyQuoteDialog = () => {
    setDialogState((prev) => ({
      ...prev,
      actionDialog: {
        show: true,
        type: Action.CopyQuote,
      },
    }));
  };

  const handleOpenSwitchOfferingDialog = () => {
    setDialogState((prev) => ({
      ...prev,
      actionDialog: {
        show: true,
        type: Action.SwitchOffering,
      },
    }));
  };

  const handleOpenReassignCustomerDialog = () => {
    setDialogState((prev) => ({
      ...prev,
      actionDialog: {
        show: true,
        type: Action.ReassignCustomer,
      },
    }));
  };

  const handleViewDuplicateQuotesDialog = () => {
    setDialogState((prev) => ({
      ...prev,
      actionDialog: {
        show: true,
        type: Action.ViewDuplicateQuotes,
      },
    }));
  };

  const handleOpenDeclineQuoteDialog = (
    quoteId: string,
    policyNumber: string,
    quoteStatus: string,
    quoteCustomerId: string,
    quoteBrokerageId: string,
    hasInternalOnlyAccessRestriction: boolean,
  ) => {
    setDialogState((prev) => ({
      ...prev,
      declineQuoteDetails: {
        declineQuoteId: quoteId,
        declineQuotePolicyNumber: policyNumber,
        declineQuoteStatus: quoteStatus,
        declineQuoteCustomerId: quoteCustomerId,
        declineQuoteBrokerageId: quoteBrokerageId,
        hasInternalOnlyAccessRestriction,
      },
    }));
  };

  const handleOpenReactivateDeclinedRejectedQuoteDialog = (
    quoteId: string,
    policyNumber: string,
    quoteStatus: string,
  ) => {
    setDialogState((prev) => ({
      ...prev,
      declinedRejectedQuoteDetails: {
        declinedRejectedQuoteId: quoteId,
        declinedRejectedQuotePolicyNumber: policyNumber,
        declinedRejectedQuoteStatus: quoteStatus,
      },
    }));
  };

  const handleConfirmDownloadApplication = async () => {
    await handleDownloadQuoteDocument();
    setDialogState((prev) => ({ ...prev, confirmDialog: { ...prev.confirmDialog, show: false } }));
  };

  const handleRemoveBrokerAccess = async () => {
    try {
      await updateQuote({ quoteId: quote.id, hasInternalOnlyAccessRestriction: true }).then(() => {
        setDialogState(initialDialogState);
        enqueueSnackbar(`Successfully removed Broker access.`, {
          preventDuplicate: true,
          variant: ToastType.Success,
        });
      });
    } catch {
      enqueueSnackbar(`Failed to remove Broker access.`, {
        preventDuplicate: true,
        variant: ToastType.Error,
      });
    }
  };

  const quoteStatus = quote?.status.id;
  const policyNumber = quote?.policyNumber?.policyNumber;
  const quoteCustomerId = quote?.customer.id;
  const quoteBrokerageId = quote?.brokerage.id;
  const hasInternalOnlyAccessRestriction = quote?.hasInternalOnlyAccessRestriction ?? false;

  const checkForUnsavedChanges = () => {
    const { attributesById } = quoteContextStore;
    return Object.keys(attributesById).length > 0;
  };

  const handleExit = () => {
    const hasUnsavedChanges = checkForUnsavedChanges();
    if (hasUnsavedChanges) {
      setDialogState((prev) => ({
        ...prev,
        confirmDialog: {
          show: true,
          description: 'There are unsaved changes. Exit without saving?',
          confirmText: 'Yes, exit without saving',
          cancelText: 'No, return to quote',
          navigatePath: '/',
        },
      }));

      return;
    }
    onConfirmExit();
  };

  return {
    [Action.AcceptQuote]: () =>
      setDialogState((prev) => ({
        ...prev,
        actionDialog: {
          show: true,
          type: Action.AcceptQuote,
        },
      })),
    [Action.ManageRoles]: () =>
      setDialogState((prev) => ({
        ...prev,
        actionDialog: {
          show: true,
          type: Action.ManageRoles,
        },
      })),
    [Action.GrantCustomerAccess]: () =>
      setDialogState((prev) => ({
        ...prev,
        actionDialog: {
          show: true,
          type: Action.GrantCustomerAccess,
        },
      })),
    [Action.ResetQuoteDefaults]: () =>
      setDialogState((prev) => ({
        ...prev,
        actionDialog: {
          show: true,
          type: Action.ResetQuoteDefaults,
        },
      })),
    [Action.ReleaseQuote]: () => {
      setDialogState((prev) => ({
        ...prev,
        actionDialog: {
          show: true,
          type: Action.ReleaseQuote,
        },
      }));
    },
    [Action.RemoveBrokerAccess]: () => {
      setDialogState((prev) => ({
        ...prev,
        confirmDialog: {
          show: true,
          description:
            'Are you sure you want to remove Broker access to this quote? All brokerage users will lose access immediately.',
          confirmText: 'Yes, remove access',
          cancelText: 'No',
          navigatePath: '',
          onConfirmation: handleRemoveBrokerAccess,
        },
      }));
    },
    [Action.DownloadApplication]: () => {
      const hasUnsavedChanges = checkForUnsavedChanges();
      if (hasUnsavedChanges) {
        setDialogState((prev) => ({
          ...prev,
          confirmDialog: {
            show: true,
            description: 'Quote has unsaved changes that will not appear in the export. Would you like to proceed?',
            confirmText: 'Yes, download',
            cancelText: 'No',
            navigatePath: '?',
            onConfirmation: handleConfirmDownloadApplication,
          },
        }));

        return;
      }
      handleConfirmDownloadApplication();
    },
    [Action.ViewExternalSentEmails]: () =>
      setDialogState((prev) => ({
        ...prev,
        actionDialog: {
          show: true,
          type: Action.ViewExternalSentEmails,
        },
      })),
    [Action.ViewQuoteDeclarations]: () =>
      setDialogState((prev) => ({
        ...prev,
        actionDialog: {
          show: true,
          type: Action.ViewQuoteDeclarations,
        },
      })),
    [Action.CancelQuote]: () => handleOpenCancelQuoteDialog(quoteId as string, policyNumber as string),
    [Action.ViewIneligibilities]: () => {
      setDialogState((prev) => ({
        ...prev,
        isQuoteIneligibilitiesOpen: true,
      }));
    },
    [Action.RateChangeAnalysis]: () => {
      setDialogState((prev) => ({
        ...prev,
        isRateChangeAnalysisOpen: true,
      }));
    },
    [Action.ToggleLockQuote]: () => updateQuote({ quoteId, hasUnderwritingLock: !quote?.hasUnderwritingLock }),
    [Action.DeclineQuote]: () =>
      handleOpenDeclineQuoteDialog(
        quoteId as string,
        policyNumber as string,
        quoteStatus as string,
        quoteCustomerId as string,
        quoteBrokerageId as string,
        hasInternalOnlyAccessRestriction as boolean,
      ),
    [Action.ReactivateQuote]: () => {
      handleOpenReactivateQuoteDialog();
    },
    [Action.ReactivateDeclinedRejectedQuote]: () =>
      handleOpenReactivateDeclinedRejectedQuoteDialog(quoteId as string, policyNumber as string, quoteStatus as string),
    [Action.ExitQuote]: () => {
      handleExit();
    },
    [Action.ReviewUnderwritingIssues]: () => {
      toggleDrawer(DrawerList.Underwriting);
      if (isMobile) {
        openHeaderMenu();
      }
      setSecondaryMenuTab(DrawerList.Underwriting);
      if (isFullScreenMode && setActiveTab) {
        setActiveTab(DrawerList.Underwriting);
      }
    },
    [Action.AddNote]: () => {
      toggleDrawer(DrawerList.Notes);
      if (isMobile) {
        openHeaderMenu();
      }
      setSecondaryMenuTab(DrawerList.Notes);
      if (isFullScreenMode && setActiveTab) {
        setActiveTab(DrawerList.Notes);
      }
    },
    [Action.MessageBroker]: () => {
      toggleDrawer(DrawerList.Messages);
      if (isMobile) {
        openHeaderMenu();
      }
      setSecondaryMenuTab(DrawerList.Messages);
      if (isFullScreenMode && setActiveTab) {
        setActiveTab(DrawerList.Messages);
      }
    },
    [Action.SaveChanges]: () => {
      saveChanges();
    },
    [Action.OpenChangeSummary]: () => {
      setDialogState((prev) => ({ ...prev, showChangelogDialog: true }));
    },
    [Action.CopyQuote]: () => {
      handleOpenCopyQuoteDialog();
    },
    [Action.SwitchOffering]: () => {
      handleOpenSwitchOfferingDialog();
    },
    [Action.ReassignCustomer]: () => {
      handleOpenReassignCustomerDialog();
    },
    [Action.ViewDuplicateQuotes]: () => {
      handleViewDuplicateQuotesDialog();
    },
    [Action.ReleaseQuoteToPolicyHolder]: () => {
      const handleReleaseQuoteToPolicyHolder = () => {
        updateQuote({ quoteId: quote.id, releasedToPolicyHolder: !quote?.releasedToPolicyHolder }).then(() => {
          const releasedToPolicyHolder = !quote?.releasedToPolicyHolder;
          enqueueSnackbar(
            releasedToPolicyHolder
              ? `Successfully marked quote as released to policyholder`
              : `Successfully unmarked quote as released to policyholder`,
            {
              preventDuplicate: true,
              variant: ToastType.Success,
            },
          );
        });
      };

      if (quote?.releasedToPolicyHolder) {
        setDialogState((prev) => ({
          ...prev,
          confirmDialog: {
            show: true,
            description: `Release Quote ${quote.policyNumber?.policyNumber} to Policyholder`,
            confirmText: 'Yes',
            cancelText: 'Exit',
            navigatePath: '?',
            checkboxText: 'I am sure I want to turn off the Release To Policyholder flag',
            onConfirmation: () => {
              handleReleaseQuoteToPolicyHolder();
            },
          },
        }));
      } else {
        setDialogState((prev) => ({
          ...prev,
          confirmDialog: {
            show: true,
            description: `Release Quote ${quote.policyNumber?.policyNumber} to Policyholder`,
            confirmText: 'Accept',
            cancelText: 'Exit',
            navigatePath: '?',
            checkboxText: [
              'I am sure I want to turn on the Release To Policyholder flag',
              'I acknowledge this will send an email to the Policyholder',
            ],
            onConfirmation: () => {
              handleReleaseQuoteToPolicyHolder();
            },
          },
        }));
      }
    },
    [Action.MarkAsReferredToBroker]: () => {
      const handleMarkAsReferToBroker = () => {
        updateQuote({ quoteId: quote.id, referredToBroker: !quote?.referredToBroker }).then(() => {
          const referredToBroker = !quote?.referredToBroker;
          enqueueSnackbar(
            referredToBroker
              ? `Successfully marked quote as referred quote to broker`
              : 'Successfully unmarked quote as referred quote to broker',
            {
              preventDuplicate: true,
              variant: ToastType.Success,
            },
          );
        });
      };

      if (quote?.referredToBroker) {
        setDialogState((prev) => ({
          ...prev,
          confirmDialog: {
            show: true,
            description: `Refer Quote ${quote.policyNumber?.policyNumber} to Broker`,
            confirmText: 'Yes',
            cancelText: 'Exit',
            navigatePath: '?',
            checkboxText: 'I am sure I want to turn off the Referred To Broker flag',
            onConfirmation: () => {
              handleMarkAsReferToBroker();
            },
          },
        }));
      } else {
        setDialogState((prev) => ({
          ...prev,
          confirmDialog: {
            show: true,
            description: `Refer Quote ${quote.policyNumber?.policyNumber} to Broker`,
            confirmText: 'Yes',
            cancelText: 'Exit',
            navigatePath: '?',
            checkboxText: 'I am sure I want to turn on the Referred To Broker flag',
            onConfirmation: () => {
              handleMarkAsReferToBroker();
            },
          },
        }));
      }
    },
    [Action.GenerateDocument]: () => {
      setDialogState((prev) => ({
        ...prev,
        actionDialog: {
          show: true,
          type: Action.GenerateDocument,
        },
      }));
    },
    [Action.GenerateConditionalRenewal]: () => {
      setDialogState((prev) => ({
        ...prev,
        actionDialog: {
          show: true,
          type: Action.GenerateConditionalRenewal,
        },
      }));
    },
    [Action.MarkForNonrenewal]: () => {
      setDialogState((prev) => ({
        ...prev,
        actionDialog: {
          show: true,
          type: Action.MarkForNonrenewal,
        },
      }));
    },
    [Action.ConvertRenewalToNewBusiness]: () => {
      setDialogState((prev) => ({
        ...prev,
        actionDialog: {
          show: true,
          type: Action.ConvertRenewalToNewBusiness,
        },
      }));
    },
  };
};
